import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft,
  GripVertical,
  Plus,
  Trash2,
  FolderPlus,
  ChevronDown,
  ChevronRight,
  Settings,
  Save,
  LayoutDashboard,
  AlertTriangle,
  Activity,
  FileText,
  Users,
  Shield,
  Calendar,
  Bell,
  Network,
  MapPin,
  BarChart3,
  Search,
  Briefcase,
  Clock,
  Building2,
  Folder,
  FolderOpen,
  Edit2,
  X,
  Check,
  Move
} from 'lucide-react';

const iconOptions = [
  { value: 'LayoutDashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { value: 'AlertTriangle', icon: AlertTriangle, label: 'Alertas' },
  { value: 'Activity', icon: Activity, label: 'Actividad' },
  { value: 'FileText', icon: FileText, label: 'Documentos' },
  { value: 'Users', icon: Users, label: 'Usuarios' },
  { value: 'Shield', icon: Shield, label: 'Seguridad' },
  { value: 'Calendar', icon: Calendar, label: 'Calendario' },
  { value: 'Bell', icon: Bell, label: 'Notificaciones' },
  { value: 'Network', icon: Network, label: 'Red' },
  { value: 'MapPin', icon: MapPin, label: 'Ubicación' },
  { value: 'BarChart3', icon: BarChart3, label: 'Reportes' },
  { value: 'Search', icon: Search, label: 'Búsqueda' },
  { value: 'Briefcase', icon: Briefcase, label: 'Casos' },
  { value: 'Clock', icon: Clock, label: 'Tiempo' },
  { value: 'Building2', icon: Building2, label: 'Empresa' },
  { value: 'Folder', icon: Folder, label: 'Carpeta' },
];

const getIconComponent = (iconName) => {
  const found = iconOptions.find(o => o.value === iconName);
  return found ? found.icon : Folder;
};

const ModuleAdmin = () => {
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [draggedItem, setDraggedItem] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [newGroupDialog, setNewGroupDialog] = useState({ open: false, nombre: '', icono: 'Folder' });
  const [newSectionDialog, setNewSectionDialog] = useState({ 
    open: false, 
    nombre: '', 
    ruta: '', 
    icono: 'FileText',
    activo: true 
  });
  const [editSectionDialog, setEditSectionDialog] = useState({
    open: false,
    section: null,
    nombre: '',
    ruta: '',
    icono: 'FileText',
    activo: true
  });
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadModules();
  }, []);

  useEffect(() => {
    if (selectedModule) {
      loadSections(selectedModule.id);
    }
  }, [selectedModule]);

  const loadModules = async () => {
    try {
      const res = await api.get('/modules');
      setModules(res.data);
      if (res.data.length > 0 && !selectedModule) {
        setSelectedModule(res.data[0]);
      }
    } catch (error) {
      toast.error('Error al cargar módulos');
    } finally {
      setLoading(false);
    }
  };

  const loadSections = async (moduleId) => {
    try {
      const res = await api.get(`/modules/${moduleId}/sections`);
      // Ordenar y estructurar secciones con grupos
      const sorted = (res.data || []).sort((a, b) => a.orden - b.orden);
      setSections(sorted);
    } catch (error) {
      toast.error('Error al cargar secciones');
    }
  };

  const handleDragStart = (e, section) => {
    setDraggedItem(section);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, targetSection) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.id === targetSection.id) return;
  };

  const handleDrop = (e, targetSection) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.id === targetSection.id) return;

    const newSections = [...sections];
    const dragIndex = newSections.findIndex(s => s.id === draggedItem.id);
    const targetIndex = newSections.findIndex(s => s.id === targetSection.id);

    // Reordenar
    const [removed] = newSections.splice(dragIndex, 1);
    newSections.splice(targetIndex, 0, removed);

    // Actualizar orden
    newSections.forEach((s, i) => {
      s.orden = i + 1;
    });

    setSections(newSections);
    setDraggedItem(null);
    setHasChanges(true);
  };

  const handleSaveOrder = async () => {
    setSaving(true);
    try {
      const reorderData = sections.map(s => ({ id: s.id, orden: s.orden }));
      await api.post(`/modules/${selectedModule.id}/sections/reorder`, { sections: reorderData });
      toast.success('Orden guardado correctamente');
      setHasChanges(false);
    } catch (error) {
      toast.error('Error al guardar orden');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupDialog.nombre.trim()) {
      toast.error('Ingrese un nombre para el grupo');
      return;
    }

    try {
      const res = await api.post(`/modules/${selectedModule.id}/groups`, {
        nombre: newGroupDialog.nombre,
        icono: newGroupDialog.icono,
        orden: sections.length + 1
      });
      
      setSections([...sections, res.data]);
      setNewGroupDialog({ open: false, nombre: '', icono: 'Folder' });
      toast.success('Grupo creado');
    } catch (error) {
      toast.error('Error al crear grupo');
    }
  };

  const handleCreateSection = async () => {
    if (!newSectionDialog.nombre.trim()) {
      toast.error('Ingrese un nombre para la sección');
      return;
    }
    if (!newSectionDialog.ruta.trim()) {
      toast.error('Ingrese una ruta para la sección');
      return;
    }

    try {
      const res = await api.post(`/modules/${selectedModule.id}/section/create`, {
        nombre: newSectionDialog.nombre,
        ruta: newSectionDialog.ruta,
        icono: newSectionDialog.icono,
        activo: newSectionDialog.activo,
        orden: sections.length + 1
      });
      
      setSections([...sections, res.data]);
      setNewSectionDialog({ open: false, nombre: '', ruta: '', icono: 'FileText', activo: true });
      toast.success('Sección creada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear sección');
    }
  };

  const handleEditSection = (section) => {
    setEditSectionDialog({
      open: true,
      section: section,
      nombre: section.nombre,
      ruta: section.ruta || '',
      icono: section.icono || 'FileText',
      activo: section.activo !== false
    });
  };

  const handleSaveEditSection = async () => {
    if (!editSectionDialog.nombre.trim()) {
      toast.error('Ingrese un nombre para la sección');
      return;
    }

    try {
      await api.put(`/modules/sections/${editSectionDialog.section.id}`, {
        nombre: editSectionDialog.nombre,
        ruta: editSectionDialog.ruta,
        icono: editSectionDialog.icono,
        activo: editSectionDialog.activo
      });
      
      setSections(sections.map(s => 
        s.id === editSectionDialog.section.id 
          ? { ...s, nombre: editSectionDialog.nombre, ruta: editSectionDialog.ruta, icono: editSectionDialog.icono, activo: editSectionDialog.activo }
          : s
      ));
      setEditSectionDialog({ open: false, section: null, nombre: '', ruta: '', icono: 'FileText', activo: true });
      toast.success('Sección actualizada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar sección');
    }
  };

  const handleDeleteSection = async (section) => {
    if (section.es_grupo) {
      // Eliminar grupo
      if (!window.confirm(`¿Eliminar el grupo "${section.nombre}"? Las secciones dentro volverán al nivel principal.`)) return;
      
      try {
        await api.delete(`/modules/${selectedModule.id}/groups/${section.id}`);
        setSections(sections.filter(s => s.id !== section.id).map(s => 
          s.grupo_padre_id === section.id ? { ...s, grupo_padre_id: null } : s
        ));
        toast.success('Grupo eliminado');
      } catch (error) {
        toast.error('Error al eliminar grupo');
      }
    } else {
      // Eliminar sección
      if (!window.confirm(`¿Eliminar la sección "${section.nombre}"?`)) return;
      
      try {
        await api.delete(`/modules/sections/${section.id}`);
        setSections(sections.filter(s => s.id !== section.id));
        toast.success('Sección eliminada');
      } catch (error) {
        toast.error('Error al eliminar sección');
      }
    }
  };

  const handleAssignToGroup = async (sectionId, groupId) => {
    try {
      if (groupId) {
        await api.post(`/modules/${selectedModule.id}/groups/${groupId}/assign`, {
          section_ids: [sectionId]
        });
      } else {
        await api.post(`/modules/sections/${sectionId}/ungroup`);
      }
      
      setSections(sections.map(s => 
        s.id === sectionId ? { ...s, grupo_padre_id: groupId || null } : s
      ));
      toast.success(groupId ? 'Sección asignada al grupo' : 'Sección desagrupada');
    } catch (error) {
      toast.error('Error al modificar grupo');
    }
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  // Organizar secciones en estructura de árbol
  const groups = sections.filter(s => s.es_grupo);
  const topLevelSections = sections.filter(s => !s.es_grupo && !s.grupo_padre_id);
  const getSectionsInGroup = (groupId) => sections.filter(s => s.grupo_padre_id === groupId);

  const renderSection = (section, isChild = false) => {
    const IconComponent = getIconComponent(section.icono);
    const isEditing = editingSection === section.id;

    return (
      <div
        key={section.id}
        draggable={!isChild}
        onDragStart={(e) => handleDragStart(e, section)}
        onDragOver={(e) => handleDragOver(e, section)}
        onDrop={(e) => handleDrop(e, section)}
        className={`flex items-center gap-3 p-3 bg-white border rounded-lg mb-2 transition-all ${
          draggedItem?.id === section.id ? 'opacity-50 border-purple-400' : 'hover:border-purple-300'
        } ${isChild ? 'ml-8 border-l-4 border-l-purple-200' : ''}`}
      >
        {!isChild && (
          <GripVertical size={16} className="text-slate-400 cursor-grab flex-shrink-0" />
        )}
        
        <div className={`p-2 rounded ${section.es_grupo ? 'bg-amber-100' : 'bg-purple-100'}`}>
          {section.es_grupo ? (
            expandedGroups[section.id] ? <FolderOpen size={16} className="text-amber-600" /> : <Folder size={16} className="text-amber-600" />
          ) : (
            <IconComponent size={16} className="text-purple-600" />
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <p className="font-medium text-slate-900 truncate">{section.nombre}</p>
          <p className="text-xs text-slate-500 truncate">
            {section.es_grupo ? `${getSectionsInGroup(section.id).length} secciones` : section.ruta || section.key}
          </p>
        </div>
        
        <Badge className={section.activo ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}>
          {section.activo ? 'Activo' : 'Inactivo'}
        </Badge>
        
        <div className="flex items-center gap-1">
          {!section.es_grupo && (
            <>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => handleEditSection(section)}
                className="h-8 px-2 text-blue-600 border-blue-200 hover:bg-blue-50"
                title="Editar sección"
              >
                <Edit2 size={14} className="mr-1" />
                Editar
              </Button>
              <Select 
                value={section.grupo_padre_id || 'none'} 
                onValueChange={(v) => handleAssignToGroup(section.id, v === 'none' ? null : v)}
              >
                <SelectTrigger className="w-32 h-8 text-xs">
                  <SelectValue placeholder="Sin grupo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin grupo</SelectItem>
                  {groups.map(g => (
                    <SelectItem key={g.id} value={g.id}>{g.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )}
          
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => handleDeleteSection(section)}
            className="text-red-500 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 size={14} />
          </Button>
        </div>
        
        {section.es_grupo && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => toggleGroup(section.id)}
          >
            {expandedGroups[section.id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </Button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-4 mb-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft size={16} className="mr-1" />
            Volver
          </Button>
        </div>
        
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Settings className="text-purple-600" />
          Administrar Módulos
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Organiza las secciones del menú lateral: reordena, agrupa o elimina
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Lista de módulos */}
        <Card className="p-4 bg-white lg:col-span-1">
          <h2 className="text-sm font-semibold text-slate-700 mb-3">Módulos</h2>
          <div className="space-y-2">
            {modules.map(module => (
              <Button
                key={module.id}
                variant={selectedModule?.id === module.id ? "default" : "outline"}
                className={`w-full justify-start ${selectedModule?.id === module.id ? 'text-white' : ''}`}
                style={selectedModule?.id === module.id ? { backgroundColor: module.color || '#7c3aed' } : {}}
                onClick={() => setSelectedModule(module)}
              >
                {module.nombre}
              </Button>
            ))}
          </div>
        </Card>

        {/* Editor de secciones */}
        <Card className="p-4 bg-white lg:col-span-3">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                {selectedModule?.nombre || 'Seleccione un módulo'}
              </h2>
              <p className="text-xs text-slate-500">
                Arrastra para reordenar • Asigna a grupos para crear submenús
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setNewSectionDialog({ ...newSectionDialog, open: true })}
                style={{ borderColor: '#7c3aed', color: '#7c3aed' }}
              >
                <Plus size={14} className="mr-1" />
                Nueva Sección
              </Button>
              
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setNewGroupDialog({ ...newGroupDialog, open: true })}
              >
                <FolderPlus size={14} className="mr-1" />
                Nuevo Grupo
              </Button>
              
              {hasChanges && (
                <Button 
                  size="sm"
                  onClick={handleSaveOrder}
                  disabled={saving}
                  style={{ backgroundColor: '#7c3aed', color: 'white' }}
                >
                  {saving ? <Clock size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />}
                  Guardar Orden
                </Button>
              )}
            </div>
          </div>

          {/* Secciones */}
          <div className="space-y-1">
            {/* Grupos */}
            {groups.map(group => (
              <div key={group.id}>
                {renderSection(group)}
                {expandedGroups[group.id] && (
                  <div className="mb-4">
                    {getSectionsInGroup(group.id).length > 0 ? (
                      getSectionsInGroup(group.id).map(s => renderSection(s, true))
                    ) : (
                      <p className="ml-8 text-xs text-slate-400 italic py-2">
                        Arrastra secciones aquí o usa el selector de grupo
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
            
            {/* Secciones sin grupo */}
            {topLevelSections.map(section => renderSection(section))}
          </div>

          {sections.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              <Settings size={48} className="mx-auto mb-3 opacity-30" />
              <p>No hay secciones configuradas</p>
            </div>
          )}
        </Card>
      </div>

      {/* Modal nuevo grupo */}
      {newGroupDialog.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setNewGroupDialog({ ...newGroupDialog, open: false })}>
          <Card className="w-full max-w-md m-4 bg-white p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <FolderPlus size={20} className="text-amber-600" />
              Crear Nuevo Grupo
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">
                  Nombre del grupo
                </label>
                <Input
                  value={newGroupDialog.nombre}
                  onChange={(e) => setNewGroupDialog({ ...newGroupDialog, nombre: e.target.value })}
                  placeholder="Ej: Reportes, Configuración..."
                  style={{ backgroundColor: '#fff' }}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">
                  Ícono
                </label>
                <Select 
                  value={newGroupDialog.icono} 
                  onValueChange={(v) => setNewGroupDialog({ ...newGroupDialog, icono: v })}
                >
                  <SelectTrigger style={{ backgroundColor: '#fff' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {iconOptions.map(opt => {
                      const Icon = opt.icon;
                      return (
                        <SelectItem key={opt.value} value={opt.value}>
                          <div className="flex items-center gap-2">
                            <Icon size={16} />
                            <span>{opt.label}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex gap-2 mt-6 justify-end">
              <Button variant="outline" onClick={() => setNewGroupDialog({ ...newGroupDialog, open: false })}>
                Cancelar
              </Button>
              <Button 
                onClick={handleCreateGroup}
                style={{ backgroundColor: '#7c3aed', color: 'white' }}
              >
                <Plus size={14} className="mr-1" />
                Crear Grupo
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Modal nueva sección */}
      {newSectionDialog.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setNewSectionDialog({ ...newSectionDialog, open: false })}>
          <Card className="w-full max-w-md m-4 bg-white p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Plus size={20} className="text-purple-600" />
              Crear Nueva Sección
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">
                  Nombre de la sección *
                </label>
                <Input
                  value={newSectionDialog.nombre}
                  onChange={(e) => setNewSectionDialog({ ...newSectionDialog, nombre: e.target.value })}
                  placeholder="Ej: F8125 AFIP, Dashboard..."
                  style={{ backgroundColor: '#fff' }}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">
                  Ruta / Link *
                </label>
                <Input
                  value={newSectionDialog.ruta}
                  onChange={(e) => setNewSectionDialog({ ...newSectionDialog, ruta: e.target.value })}
                  placeholder="Ej: /m/reportes/f8125"
                  style={{ backgroundColor: '#fff' }}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Ruta completa donde navega el link (debe comenzar con /m/)
                </p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">
                  Ícono
                </label>
                <Select 
                  value={newSectionDialog.icono} 
                  onValueChange={(v) => setNewSectionDialog({ ...newSectionDialog, icono: v })}
                >
                  <SelectTrigger style={{ backgroundColor: '#fff' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {iconOptions.map(opt => {
                      const Icon = opt.icon;
                      return (
                        <SelectItem key={opt.value} value={opt.value}>
                          <div className="flex items-center gap-2">
                            <Icon size={16} />
                            <span>{opt.label}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={newSectionDialog.activo}
                  onChange={(e) => setNewSectionDialog({ ...newSectionDialog, activo: e.target.checked })}
                  className="rounded border-slate-300"
                  id="newSectionActivo"
                />
                <label htmlFor="newSectionActivo" className="text-sm text-slate-700">
                  Sección activa (visible en el menú)
                </label>
              </div>
            </div>
            
            <div className="flex gap-2 mt-6 justify-end">
              <Button variant="outline" onClick={() => setNewSectionDialog({ ...newSectionDialog, open: false })}>
                Cancelar
              </Button>
              <Button 
                onClick={handleCreateSection}
                style={{ backgroundColor: '#7c3aed', color: 'white' }}
              >
                <Plus size={14} className="mr-1" />
                Crear Sección
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Modal editar sección */}
      {editSectionDialog.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setEditSectionDialog({ ...editSectionDialog, open: false })}>
          <Card className="w-full max-w-md m-4 bg-white p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Edit2 size={20} className="text-blue-600" />
              Editar Sección
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">
                  Nombre de la sección *
                </label>
                <Input
                  value={editSectionDialog.nombre}
                  onChange={(e) => setEditSectionDialog({ ...editSectionDialog, nombre: e.target.value })}
                  placeholder="Nombre..."
                  style={{ backgroundColor: '#fff' }}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">
                  Ruta / Link
                </label>
                <Input
                  value={editSectionDialog.ruta}
                  onChange={(e) => setEditSectionDialog({ ...editSectionDialog, ruta: e.target.value })}
                  placeholder="Ej: /m/reportes/f8125"
                  style={{ backgroundColor: '#fff' }}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">
                  Ícono
                </label>
                <Select 
                  value={editSectionDialog.icono} 
                  onValueChange={(v) => setEditSectionDialog({ ...editSectionDialog, icono: v })}
                >
                  <SelectTrigger style={{ backgroundColor: '#fff' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {iconOptions.map(opt => {
                      const Icon = opt.icon;
                      return (
                        <SelectItem key={opt.value} value={opt.value}>
                          <div className="flex items-center gap-2">
                            <Icon size={16} />
                            <span>{opt.label}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editSectionDialog.activo}
                  onChange={(e) => setEditSectionDialog({ ...editSectionDialog, activo: e.target.checked })}
                  className="rounded border-slate-300"
                  id="editSectionActivo"
                />
                <label htmlFor="editSectionActivo" className="text-sm text-slate-700">
                  Sección activa (visible en el menú)
                </label>
              </div>
            </div>
            
            <div className="flex gap-2 mt-6 justify-end">
              <Button variant="outline" onClick={() => setEditSectionDialog({ ...editSectionDialog, open: false })}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSaveEditSection}
                style={{ backgroundColor: '#2563eb', color: 'white' }}
              >
                <Check size={14} className="mr-1" />
                Guardar Cambios
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ModuleAdmin;
